import React, { useRef, useEffect, useState } from "react";
import { Card, Spinner, Row, Col, Container } from "react-bootstrap";
import Chart from "chart.js/auto";

interface ClientesStatus {
  ATIVO: string;
  INATIVO: string;
  PENDENCIA: string;
}

interface Props {
  dadosClientes: { clientes_x_status: ClientesStatus[] } | null;
  loading: boolean;
  error: string | null;
}

const StatusGaugeChart: React.FC<Props> = ({ dadosClientes, loading, error }) => {
  const chartRef = useRef<HTMLCanvasElement | null>(null);
  const [chartInstance, setChartInstance] = useState<Chart | null>(null);

  // Verifica se há dados antes de acessar os valores
  const status: ClientesStatus = dadosClientes?.clientes_x_status?.[0] ?? { ATIVO: "0", INATIVO: "0", PENDENCIA: "0" };
  const ativos = parseInt(status.ATIVO) || 0;
  const inativos = parseInt(status.INATIVO) || 0;
  const inadimplentes = parseInt(status.PENDENCIA) || 0;
  const totalClientes = ativos + inativos + inadimplentes || 1; // Evita divisão por zero

  useEffect(() => {
    if (dadosClientes?.clientes_x_status && chartRef.current) {
      const ctx = chartRef.current.getContext("2d");
      if (!ctx) return;

      // Se já existir uma instância do gráfico, destrói antes de criar uma nova
      if (chartInstance) {
        chartInstance.destroy();
      }

      const newChart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["Ativos", "Inativos", "Inadimplentes"],
          datasets: [
            {
              data: [ativos, inativos, inadimplentes],
              backgroundColor: ["#28a745", "#ffc107", "#dc3545"], // Cores dos status
              borderWidth: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "70%",
          circumference: 180,
          rotation: 270,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
        },
      });

      setChartInstance(newChart);
    }
  }, [dadosClientes]);

  return (
    <Card className="p-3 shadow" style={{minHeight: "500px"}}>
      <h5>Clientes Ativos x Inativos x Inadimplentes</h5>
      {loading ? (
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p>Carregando dados...</p>
        </div>
      ) : error ? (
        <div className="text-center text-danger">
          <p>{error}</p>
        </div>
      ) : (
        <div className="d-flex flex-column align-items-center" style={{ position: "relative", width: "100%", height: "250px" }}>
          <canvas ref={chartRef}></canvas>
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              fontSize: "clamp(14px, 4vw, 20px)", // Ajuste de fonte para diferentes tamanhos de tela
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              textAlign: "center"
            }}
          >
            {ativos} / {totalClientes}
          </div>
        </div>
      )}
      <div className="mt-3 text-center">
        <p><strong>Total de Clientes:</strong> {totalClientes}</p>
        <p><span style={{ color: "#28a745" }}>🟢 Ativos:</span> {ativos}</p>
        <p><span style={{ color: "#ffc107" }}>🟡 Inativos:</span> {inativos}</p>
        <p><span style={{ color: "#dc3545" }}>🔴 Inadimplentes:</span> {inadimplentes}</p>
      </div>
    </Card>
  );
};

export default StatusGaugeChart;
